import axios from 'axios';
import HttpStatus from 'http-status';
import type { AxiosError, AxiosResponse, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';

// There is a bug Axios' typings introduced in
// PR https://github.com/axios/axios/pull/5420
// Bug report: https://github.com/axios/axios/issues/5454
//             https://github.com/axios/axios/issues/5416
type AxiosRequestHeaders = {
  [x: string]: string | number | boolean;
};

interface IApiServiceRequest {
  url: string;
  data?: JSONArray | JSONObject | JSONObject[] | string;
  config?:
    | AxiosRequestConfig
    | InternalAxiosRequestConfig
    | {
        headers?: AxiosRequestHeaders;
        baseURL?: string;
      };
}

const baseURL = typeof window === 'undefined' ? `${process.env.URL_API_INTERNAL}` : '';

const instance = axios.create({
  baseURL: baseURL,
  timeout: 1000 * 60,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-store'
  },
  withCredentials: true
});

const requestInterceptor = (requestConfig: InternalAxiosRequestConfig) => {
  return requestConfig;
};

const responseInterceptor = (response: AxiosResponse) => {
  return response;
};

const errorInterceptor = (error: AxiosError) => {
  switch (error?.response?.status) {
    case HttpStatus.UNPROCESSABLE_ENTITY:
      return Promise.reject({
        status: error.response.status,
        body: error.response.data
      });

    case HttpStatus.UNAUTHORIZED:
      // TODO: Redirect to login
      return Promise.reject(error);

    case HttpStatus.FORBIDDEN:
      return Promise.reject(error);

    case HttpStatus.NOT_FOUND:
      return Promise.reject(error);

    case HttpStatus.INTERNAL_SERVER_ERROR:
      return Promise.reject(error);

    default:
      return Promise.reject(error);
  }
};

instance.interceptors.request.use(requestInterceptor);
instance.interceptors.response.use(responseInterceptor, errorInterceptor);

const responseBody = <T>(response: AxiosResponse<T>) => response.data;

export const api = {
  get: <T>({ url, config }: IApiServiceRequest) => instance.get<T>(url, config).then(responseBody),
  post: <T>({ url, data, config }: IApiServiceRequest) => instance.post<T>(url, data, config).then(responseBody),
  put: <T>({ url, data, config }: IApiServiceRequest) => instance.put<T>(url, data, config).then(responseBody),
  patch: <T>({ url, data, config }: IApiServiceRequest) => instance.patch<T>(url, data, config).then(responseBody),
  delete: <T>({ url, data, config }: IApiServiceRequest) => instance.put<T>(url, data, config).then(responseBody)
};
